// src/utils/sentry-analytics.ts
// Session-level analytics context manager for Sentry
// Tracks overall session metrics and user engagement patterns
// RELEVANT FILES: src/utils/sentry.ts, src/composables/analytics/useSentryAnalytics.ts

import * as Sentry from '@sentry/vue';

/**
 * Session analytics data
 */
export interface SessionAnalytics {
  sessionId: string;
  startTime: number;
  endTime?: number;
  duration?: number;
  pagesVisited: number;
  totalActions: number;
  featuresUsed: Set<string>;
  organizations: Set<string>;
  errors: number;
}

/**
 * Feature usage categories
 */
export enum FeatureCategory {
  DASHBOARD = 'dashboard',
  CALL_ANALYSIS = 'call-analysis',
  FILTERS = 'filters',
  EXPORTS = 'exports',
  AUDIO = 'audio',
  SEARCH = 'search',
  SETTINGS = 'settings',
  ORGANIZATION = 'organization',
}

let sessionAnalytics: SessionAnalytics | null = null;

/**
 * Generate a unique session ID
 */
function generateSessionId(): string {
  return `session_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
}

/**
 * User identity for Sentry tracking
 */
export interface SentryUserIdentity {
  cognitoSub?: string;
  email?: string;
  username?: string;
  role?: string;
}

/**
 * Initialize session analytics tracking with user identity
 * Call this when user logs in or app starts
 * Wrapped in try-catch per Gemini recommendation: analytics errors should never crash the app
 */
export function initializeSessionAnalytics(user?: SentryUserIdentity): void {
  try {
    const sessionId = generateSessionId();

    sessionAnalytics = {
      sessionId,
      startTime: Date.now(),
      pagesVisited: 0,
      totalActions: 0,
      featuresUsed: new Set(),
      organizations: new Set(),
      errors: 0,
    };

    // Set user identity in Sentry (best practice - uses permanent cognito_sub as id)
    if (user?.cognitoSub || user?.email) {
      Sentry.setUser({
        id: user.cognitoSub, // Permanent user ID from Cognito
        email: user.email,
        username: user.username,
      });

      // Set user role as tag for filtering in Sentry
      if (user.role) {
        Sentry.setTag('user.role', user.role);
      }
    }

    // Set session context in Sentry
    Sentry.setContext('session', {
      id: sessionId,
      startTime: new Date(sessionAnalytics.startTime).toISOString(),
    });

    // Track session start
    Sentry.addBreadcrumb({
      category: 'session',
      message: 'Session started',
      level: 'info',
      data: {
        sessionId,
        cognitoSub: user?.cognitoSub,
      },
    });
  } catch (error) {
    // Analytics error should never crash the app
    if (import.meta.env.DEV) {
      console.error('Session analytics initialization error:', error);
    }
    // In production, silently fail
  }
}

/**
 * Track page visit
 */
export function trackPageVisit(path: string, _title?: string): void {
  if (!sessionAnalytics) {
    initializeSessionAnalytics();
    if (!sessionAnalytics) {return;} // Guard against initialization failure
  }

  sessionAnalytics.pagesVisited++;

  // Determine feature from path
  let feature: string | undefined;

  if (path.includes('/dashboard')) {
    feature = FeatureCategory.DASHBOARD;
  } else if (path.includes('/calls')) {
    feature = FeatureCategory.CALL_ANALYSIS;
  } else if (path.includes('/settings')) {
    feature = FeatureCategory.SETTINGS;
  }

  if (feature) {
    sessionAnalytics.featuresUsed.add(feature);
  }

  // Update session context
  Sentry.setContext('session', {
    id: sessionAnalytics.sessionId,
    startTime: new Date(sessionAnalytics.startTime).toISOString(),
    pagesVisited: sessionAnalytics.pagesVisited,
    featuresUsed: Array.from(sessionAnalytics.featuresUsed),
  });
}

/**
 * Track feature usage
 */
export function trackFeatureUsage(feature: FeatureCategory | string): void {
  if (!sessionAnalytics) {
    initializeSessionAnalytics();
    if (!sessionAnalytics) {return;} // Guard against initialization failure
  }

  sessionAnalytics.featuresUsed.add(feature);

  // Update session context
  Sentry.setContext('session', {
    id: sessionAnalytics.sessionId,
    startTime: new Date(sessionAnalytics.startTime).toISOString(),
    pagesVisited: sessionAnalytics.pagesVisited,
    featuresUsed: Array.from(sessionAnalytics.featuresUsed),
  });
}

/**
 * Track organization access
 */
export function trackOrganizationAccess(orgId: string, orgName: string): void {
  if (!sessionAnalytics) {
    initializeSessionAnalytics();
    if (!sessionAnalytics) {return;} // Guard against initialization failure
  }

  sessionAnalytics.organizations.add(orgId);

  // Update session context
  Sentry.setContext('session', {
    id: sessionAnalytics.sessionId,
    startTime: new Date(sessionAnalytics.startTime).toISOString(),
    pagesVisited: sessionAnalytics.pagesVisited,
    featuresUsed: Array.from(sessionAnalytics.featuresUsed),
    organizations: Array.from(sessionAnalytics.organizations),
  });

  // Set current organization tags
  Sentry.setTag('organization.current', orgId);
  Sentry.setContext('organization', {
    id: orgId,
    name: orgName,
  });
}

/**
 * Track action performed
 */
export function incrementActionCount(): void {
  if (!sessionAnalytics) {
    initializeSessionAnalytics();
    if (!sessionAnalytics) {return;} // Guard against initialization failure
  }

  sessionAnalytics.totalActions++;
}

/**
 * Track error occurrence
 */
export function incrementErrorCount(): void {
  if (!sessionAnalytics) {
    initializeSessionAnalytics();
    if (!sessionAnalytics) {return;} // Guard against initialization failure
  }

  sessionAnalytics.errors++;
}

/**
 * Get current session analytics
 */
export function getSessionAnalytics(): SessionAnalytics | null {
  if (!sessionAnalytics) {return null;}

  const now = Date.now();

  return {
    ...sessionAnalytics,
    duration: now - sessionAnalytics.startTime,
  };
}

/**
 * Get session summary
 */
export function getSessionSummary(): {
  sessionId: string;
  durationMinutes: number;
  pagesVisited: number;
  totalActions: number;
  featuresUsed: string[];
  organizationsAccessed: number;
  errors: number;
  actionsPerMinute: number;
} | null {
  if (!sessionAnalytics) {return null;}

  const now = Date.now();
  const duration = now - sessionAnalytics.startTime;
  const durationMinutes = Math.round(duration / 60000);

  return {
    sessionId: sessionAnalytics.sessionId,
    durationMinutes,
    pagesVisited: sessionAnalytics.pagesVisited,
    totalActions: sessionAnalytics.totalActions,
    featuresUsed: Array.from(sessionAnalytics.featuresUsed),
    organizationsAccessed: sessionAnalytics.organizations.size,
    errors: sessionAnalytics.errors,
    actionsPerMinute:
      durationMinutes > 0 ? Math.round(sessionAnalytics.totalActions / durationMinutes) : 0,
  };
}

/**
 * End session and send summary to Sentry
 * Call this on logout or app close
 */
export function endSessionAnalytics(): void {
  if (!sessionAnalytics) {return;}

  const now = Date.now();
  sessionAnalytics.endTime = now;
  sessionAnalytics.duration = now - sessionAnalytics.startTime;

  const summary = getSessionSummary();

  if (summary) {
    // Send session summary as breadcrumb
    Sentry.addBreadcrumb({
      category: 'session',
      message: 'Session ended',
      level: 'info',
      data: summary,
    });

    // Set final session context
    Sentry.setContext('session_summary', summary);
  }

  sessionAnalytics = null;
}

// Store handlers for cleanup
const handleVisibilityChange = () => {
  if (document.hidden) {
    Sentry.addBreadcrumb({
      category: 'session',
      message: 'Page hidden',
      level: 'info',
    });
  } else {
    Sentry.addBreadcrumb({
      category: 'session',
      message: 'Page visible',
      level: 'info',
    });
  }
};

const handleFocus = () => {
  Sentry.addBreadcrumb({
    category: 'session',
    message: 'Window focused',
    level: 'info',
  });
};

const handleBlur = () => {
  Sentry.addBreadcrumb({
    category: 'session',
    message: 'Window blurred',
    level: 'info',
  });
};

const handleBeforeUnload = () => {
  endSessionAnalytics();
};

/**
 * Set up automatic session tracking
 * Call this once during app initialization
 */
export function setupSessionTracking(): void {
  // Track page visibility changes
  document.addEventListener('visibilitychange', handleVisibilityChange);

  // Track window focus
  window.addEventListener('focus', handleFocus);
  window.addEventListener('blur', handleBlur);

  // Send session summary before page unload
  window.addEventListener('beforeunload', handleBeforeUnload);
}

/**
 * Clean up session tracking listeners
 * Call this when app unmounts or for cleanup
 */
export function cleanupSessionTracking(): void {
  document.removeEventListener('visibilitychange', handleVisibilityChange);
  window.removeEventListener('focus', handleFocus);
  window.removeEventListener('blur', handleBlur);
  window.removeEventListener('beforeunload', handleBeforeUnload);
}
